import React, { useMemo } from 'react';

// Pure-SVG area sparkline — no chart library. Renders an area fill + line
// across N data points. Designed for KPI tiles so it stays readable at
// 80×24 down to 200×60. Bring your own color via `color` (defaults gold).
//
// Props:
//   data:   array of numbers (the y values)
//   width:  px (default 120)
//   height: px (default 32)
//   color:  stroke + fill color (gold by default)
//   fill:   whether to draw the area fill (default true)

const Sparkline = ({
  data = [],
  width = 120,
  height = 32,
  color = '#00e0ff',
  fill = true,
  strokeWidth = 1.5,
}) => {
  const path = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;
    const max = Math.max(...data, 0);
    const min = Math.min(...data, 0);
    const range = max - min || 1;
    const stepX = data.length > 1 ? width / (data.length - 1) : width;
    const pts = data.map((v, i) => {
      const x = i * stepX;
      // Top of chart = max value; flip y because SVG y grows downward.
      const y = height - ((Number(v) - min) / range) * height;
      return [x, isFinite(y) ? y : height];
    });
    const linePath = pts
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
      .join(' ');
    const areaPath = `${linePath} L${pts[pts.length - 1][0].toFixed(1)},${height} L0,${height} Z`;
    return { linePath, areaPath };
  }, [data, width, height]);

  if (!path) {
    return <svg width={width} height={height} aria-hidden="true" />;
  }

  const gradientId = `spark-grad-${color.replace(/[^a-z0-9]/gi, '')}-${width}-${height}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <path d={path.areaPath} fill={`url(#${gradientId})`} />}
      <path d={path.linePath} fill="none" stroke={color} strokeWidth={strokeWidth}
            strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

export default Sparkline;
